import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, CurrencyInput, Field, FieldDescription, FieldError, FieldLabel, NumberInput, Select, Skeleton } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useMemberCompensation, useSetMemberSalary } from "@/hooks/useTeam";
import { formatCurrency, formatDate } from "@/lib/format/br";
import { teamErrorKey } from "@/lib/team/api";
import type { TeamMemberDetail } from "@/lib/team/types";
import { salarySchema, type SalaryForm } from "@/lib/validations/team";

/** Plano 3 pushes the commission section here. */
export const compensationExtraSections: Array<(member: TeamMemberDetail) => JSX.Element | null> = [];

const card = "flex flex-col gap-5 rounded-card border border-border bg-card p-6";

export function MemberCompensationTab({ member }: { member: TeamMemberDetail }) {
  const { t, i18n } = useTranslation("team");
  const { hasPermission: canView } = useHasPermissionSync("commissions", "view");
  const { hasPermission: canEdit } = useHasPermissionSync("commissions", "edit");
  const allowed = canView || member.is_self;
  const { data, isLoading } = useMemberCompensation(member.user_id, allowed);
  const save = useSetMemberSalary();
  const schema = useMemo(() => salarySchema(t), [t]);
  const form = useForm<SalaryForm>({
    resolver: zodResolver(schema),
    defaultValues: { base_salary: null, salary_period: "monthly", salary_payday: null },
  });
  const { errors } = form.formState;

  useEffect(() => {
    if (data) form.reset({ base_salary: data.base_salary, salary_period: data.salary_period, salary_payday: data.salary_payday });
  }, [data, form]);

  if (!allowed) {
    return (
      <div role="alert" className="rounded-md border border-border bg-card p-6">
        <h2 className="text-base font-semibold">{t("denied.title")}</h2>
      </div>
    );
  }
  if (isLoading || !data) return <Skeleton className="h-64 w-full" label={t("member.loading")} />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await save.mutateAsync({
        userId: member.user_id, baseSalary: values.base_salary, salaryPeriod: values.salary_period, salaryPayday: values.salary_payday,
      });
      toast({ title: t("member.saved") });
    } catch (error) {
      toast({ title: t(teamErrorKey(error)), variant: "destructive" });
    }
  });

  return (
    <div className="flex flex-col gap-6">
      <section data-section="salary" className={card} aria-labelledby="team-salary">
        <div className="flex flex-col gap-1">
          <h2 id="team-salary" className="text-base font-semibold">{t("compensation.salaryTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("compensation.salaryDescription")}</p>
        </div>
        {canEdit ? (
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field invalid={!!errors.base_salary}>
                <FieldLabel>{t("compensation.baseSalary")}</FieldLabel>
                <Controller control={form.control} name="base_salary" render={({ field }) => (
                  <CurrencyInput value={field.value ?? undefined} onValueChange={field.onChange} />
                )} />
                {errors.base_salary && <FieldError>{errors.base_salary.message}</FieldError>}
              </Field>
              <Controller control={form.control} name="salary_period" render={({ field }) => (
                <Select
                  label={t("compensation.period")}
                  value={field.value}
                  onValueChange={field.onChange}
                  options={(["monthly", "biweekly", "weekly"] as const).map((v) => ({ value: v, label: t(`compensation.periods.${v}`) }))}
                />
              )} />
              <Field invalid={!!errors.salary_payday}>
                <FieldLabel>{t("compensation.payday")}</FieldLabel>
                <Controller control={form.control} name="salary_payday" render={({ field }) => (
                  <NumberInput min={1} max={31} value={field.value ?? undefined} onValueChange={field.onChange} />
                )} />
                <FieldDescription>{t("compensation.paydayHint")}</FieldDescription>
                {errors.salary_payday && <FieldError>{errors.salary_payday.message}</FieldError>}
              </Field>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending}>{t("compensation.save")}</Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div><dt className="text-muted-foreground">{t("compensation.baseSalary")}</dt><dd className="font-semibold tabular-nums">{formatCurrency(data.base_salary, i18n.language)}</dd></div>
            <div><dt className="text-muted-foreground">{t("compensation.period")}</dt><dd>{t(`compensation.periods.${data.salary_period}`)}</dd></div>
            <div><dt className="text-muted-foreground">{t("compensation.payday")}</dt><dd>{data.salary_payday ?? "—"}</dd></div>
            <p className="text-xs text-muted-foreground sm:col-span-3">{t("compensation.readOnly")}</p>
          </dl>
        )}
        <details className="text-sm">
          <summary className="flex min-h-8 cursor-pointer items-center font-medium">
            {t("compensation.history", { count: data.history.length })}
          </summary>
          {data.history.length === 0 ? (
            <p className="pt-2 text-muted-foreground">{t("compensation.historyEmpty")}</p>
          ) : (
            <ul className="flex flex-col gap-2 pt-2">
              {data.history.map((h, i) => (
                <li key={`${h.effective_from}-${i}`} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{formatDate(h.effective_from, i18n.language)} · {t(`compensation.reasons.${h.reason}`)}</span>
                  <span className="tabular-nums">{formatCurrency(h.amount, i18n.language)}</span>
                </li>
              ))}
            </ul>
          )}
        </details>
      </section>
      {compensationExtraSections.map((render, i) => (
        <div key={i}>{render(member)}</div>
      ))}
    </div>
  );
}
